import React, { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import styled, { keyframes, css } from "styled-components";

/**
 * Room of Requirement — the 404 page.
 *
 * In the books the door only appears after you walk past the stretch of wall
 * three times, concentrating on what you need. So does this one.
 */

const PACES_NEEDED = 3;

const NotFound: React.FC = () => {
  const [paces, setPaces] = useState(0);
  const revealed = paces >= PACES_NEEDED;

  const pace = useCallback(() => {
    setPaces((p) => Math.min(p + 1, PACES_NEEDED));
  }, []);

  return (
    <Corridor>
      <Torch $side="left" />
      <Torch $side="right" />

      <Inner>
        <Code>404</Code>
        <Title>The Room of Requirement</Title>
        <Blurb>
          You have come to a stretch of blank wall on the seventh floor. Whatever
          you were looking for, it is not here &mdash; at least, not yet.
        </Blurb>

        <WallScene>
          <Wall $revealed={revealed}>
            <Door $revealed={revealed}>
              <DoorHandle />
              <DoorPanel />
              <DoorPanel />
            </Door>
          </Wall>
          <Footsteps>
            {Array.from({ length: PACES_NEEDED }).map((_, i) => (
              <Step key={i} $lit={i < paces}>
                {i % 2 === 0 ? "\u{1F463}" : "\u{1F463}"}
              </Step>
            ))}
          </Footsteps>
        </WallScene>

        {!revealed ? (
          <>
            <Hint>
              Walk past it {PACES_NEEDED - paces} more time
              {PACES_NEEDED - paces === 1 ? "" : "s"}, thinking hard about where
              you meant to go.
            </Hint>
            <PaceButton onClick={pace}>Pace the corridor</PaceButton>
          </>
        ) : (
          <>
            <Hint $found>A door has appeared in the wall.</Hint>
            <EnterLink to="/">Step through &rarr;</EnterLink>
          </>
        )}
      </Inner>
    </Corridor>
  );
};

export default NotFound;

/* ==================== styles ==================== */

const flicker = keyframes`
  0%, 100% { opacity: 0.85; transform: scale(1); }
  45%      { opacity: 1;    transform: scale(1.08); }
  70%      { opacity: 0.7;  transform: scale(0.96); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const doorAppear = keyframes`
  from { opacity: 0; transform: scaleY(0.2); filter: blur(6px); }
  to   { opacity: 1; transform: scaleY(1);   filter: blur(0); }
`;

const Corridor = styled.section`
  position: relative;
  min-height: 88vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5rem 1.5rem;
  overflow: hidden;

  /* Cold stone corridor */
  background:
    radial-gradient(circle at 50% 40%, rgba(196, 148, 42, 0.06), transparent 60%),
    repeating-linear-gradient(
      0deg,
      rgba(255, 255, 255, 0.014) 0px,
      rgba(255, 255, 255, 0.014) 2px,
      transparent 2px,
      transparent 54px
    );
`;

const Torch = styled.div<{ $side: "left" | "right" }>`
  position: absolute;
  top: 22%;
  ${({ $side }) => ($side === "left" ? "left: 8%;" : "right: 8%;")}
  width: 90px;
  height: 90px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 170, 60, 0.28), transparent 70%);
  filter: blur(10px);
  animation: ${flicker} 2.6s ease-in-out infinite;
  pointer-events: none;

  @media (max-width: 768px) { display: none; }
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 560px;
  text-align: center;
  animation: ${fadeUp} 0.6s ease both;
`;

const Code = styled.p`
  font-family: "Cinzel", serif;
  font-size: 0.72rem;
  letter-spacing: 0.4em;
  color: #6a5450;
`;

const Title = styled.h1`
  font-family: "Cinzel", serif;
  font-size: clamp(1.6rem, 4.5vw, 2.5rem);
  font-weight: 700;
  color: #f0e6dc;
  margin: 0.4rem 0 0.9rem;
  text-shadow: 0 0 22px rgba(196, 148, 42, 0.25);
`;

const Blurb = styled.p`
  font-family: "Crimson Text", serif;
  font-size: 1rem;
  line-height: 1.75;
  color: #a08e84;
  margin-bottom: 2.2rem;
`;

const WallScene = styled.div`
  margin-bottom: 1.8rem;
`;

const Wall = styled.div<{ $revealed: boolean }>`
  position: relative;
  height: 190px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  background:
    repeating-linear-gradient(90deg, rgba(0,0,0,0.25) 0 1px, transparent 1px 78px),
    repeating-linear-gradient(0deg, rgba(0,0,0,0.25) 0 1px, transparent 1px 42px),
    linear-gradient(160deg, #241f28, #16121a);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  overflow: hidden;
  transition: box-shadow 0.6s ease;

  ${({ $revealed }) => $revealed && css`
    box-shadow: inset 0 0 44px rgba(196, 148, 42, 0.22);
  `}
`;

const Door = styled.div<{ $revealed: boolean }>`
  width: 96px;
  height: 152px;
  border-radius: 48px 48px 4px 4px;
  background: linear-gradient(170deg, #5a3a1c, #3a2410);
  border: 2px solid #c4942a;
  box-shadow: 0 0 26px rgba(196, 148, 42, 0.45), inset 0 0 18px rgba(0, 0, 0, 0.55);
  display: ${({ $revealed }) => ($revealed ? "flex" : "none")};
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transform-origin: bottom center;
  animation: ${doorAppear} 0.9s cubic-bezier(0.22, 1, 0.36, 1) both;
`;

const DoorPanel = styled.div`
  width: 56px;
  height: 34px;
  border: 1px solid rgba(196, 148, 42, 0.35);
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.18);
`;

const DoorHandle = styled.div`
  position: absolute;
  right: 14px;
  top: 52%;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #d9b45a;
  box-shadow: 0 0 8px rgba(217, 180, 90, 0.8);
`;

const Footsteps = styled.div`
  display: flex;
  justify-content: center;
  gap: 1.4rem;
  margin-top: 1rem;
`;

const Step = styled.span<{ $lit: boolean }>`
  font-size: 1rem;
  transition: opacity 0.4s ease, filter 0.4s ease, transform 0.4s ease;
  opacity: ${({ $lit }) => ($lit ? 1 : 0.16)};
  filter: ${({ $lit }) => ($lit ? "none" : "grayscale(1)")};
  transform: ${({ $lit }) => ($lit ? "translateY(-2px)" : "none")};
`;

const Hint = styled.p<{ $found?: boolean }>`
  font-family: "Caveat", cursive;
  font-size: 1.15rem;
  color: ${({ $found }) => ($found ? "#d3a625" : "#8a7a72")};
  margin-bottom: 1.2rem;
`;

const buttonBase = css`
  display: inline-block;
  font-family: "Cinzel", serif;
  font-size: 0.78rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 0.75rem 1.7rem;
  border-radius: 4px;
  border: 1px solid rgba(196, 148, 42, 0.45);
  background: rgba(196, 148, 42, 0.07);
  color: #e2cf9a;
  cursor: none;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(196, 148, 42, 0.16);
    border-color: rgba(196, 148, 42, 0.8);
    box-shadow: 0 0 22px rgba(196, 148, 42, 0.22);
  }
`;

const PaceButton = styled.button`
  ${buttonBase}
`;

const EnterLink = styled(Link)`
  ${buttonBase}
`;
